import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { SessionService } from '../services/session.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const refreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const session = inject(SessionService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  const isAuthEndpoint =
    req.url.includes('/login') ||
    req.url.includes('/refresh') ||
    req.url.includes('/logout');

  return next(req).pipe(
    catchError((err: unknown) => {
      // Si no es error HTTP, no tocamos nada
      if (!(err instanceof HttpErrorResponse)) {
        return throwError(() => err);
      }

      // ✅ 403: no permisos → logout + toast
      if (err.status === 403) {
        toastr.error('No tenés permisos para acceder a este sitio.');
        session.clearSession();
        router.navigate(['/login']);
        return throwError(() => err);
      }

      // ✅ Si NO es 401, no hacemos nada especial
      if (err.status !== 401) {
        return throwError(() => err);
      }

      // ✅ Si es endpoint de auth, NO intentamos refresh (evita loops)
      if (isAuthEndpoint) {
        session.clearSession();
        router.navigate(['/login']);
        return throwError(() => err);
      }

      // ✅ Si no tengo refresh token, no puedo refrescar
      const refreshToken = session.getRefreshToken();
      if (!refreshToken) {
        session.clearSession();
        router.navigate(['/login']);
        return throwError(() => err);
      }

      // --- Refresh flow con cola (evita 10 refresh a la vez) ---
      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        return auth.refreshAccessToken().pipe(
          switchMap((resp) => {
            const payload = resp?.data ?? resp; // ✅ soporta {data:{...}} o {...}
            const newAccess = payload?.accessToken;
            const newRefresh = payload?.refreshToken;

            if (!newAccess || !newRefresh) {
              // respuesta inválida
              session.clearSession();
              router.navigate(['/login']);
              return throwError(() => err);
            }

            // guardamos tokens nuevos (rotativo)
            session.updateTokens(newAccess, newRefresh);

            isRefreshing = false;
            refreshTokenSubject.next(newAccess);

            // reintenta el request original con el token nuevo
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newAccess}` },
            });

            return next(retryReq);
          }),
          catchError((refreshErr) => {
            isRefreshing = false;
            session.clearSession();
            router.navigate(['/login']);
            return throwError(() => refreshErr);
          }),
        );
      } else {
        // Si ya se está refrescando, esperamos a que termine y reintentamos
        return refreshTokenSubject.pipe(
          filter((token) => token !== null),
          take(1),
          switchMap((token) => {
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
            });
            return next(retryReq);
          }),
        );
      }
    }),
  );
};
